import { useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import { Input } from "./ui/input";

let suburbCache = null;
let suburbRequest = null;

const loadSuburbs = () => {
  if (suburbCache) return Promise.resolve(suburbCache);
  if (!suburbRequest) {
    suburbRequest = fetch("/data/au-suburbs.json")
      .then((response) => (response.ok ? response.json() : []))
      .then((rows) => {
        suburbCache = Array.isArray(rows) ? rows : [];
        return suburbCache;
      })
      .catch(() => []);
  }
  return suburbRequest;
};

export const SuburbAutocomplete = ({
  value = "",
  onChange,
  onSelect,
  includePostcodeInValue = false,
  placeholder = "Start typing a suburb or postcode",
  id,
  required = false,
  testId = "suburb-autocomplete",
}) => {
  const [options, setOptions] = useState(suburbCache || []);
  const [open, setOpen] = useState(false);

  const ensureLoaded = () => {
    loadSuburbs().then((rows) => setOptions(rows));
  };

  const suggestions = useMemo(() => {
    const query = value.trim().toLowerCase();
    if (query.length < 2 || !options.length) return [];
    const starts = [];
    const contains = [];
    for (const option of options) {
      const name = (option.s || "").toLowerCase();
      const postcode = String(option.p || "");
      if (name.startsWith(query) || postcode.startsWith(query)) starts.push(option);
      else if (name.includes(query)) contains.push(option);
      if (starts.length >= 8) break;
    }
    return starts.length >= 8 ? starts.slice(0, 8) : starts.concat(contains).slice(0, 8);
  }, [options, value]);

  const choose = (option) => {
    const nextValue = includePostcodeInValue
      ? option.s + ", " + option.p
      : option.s;
    onChange(nextValue);
    if (onSelect) onSelect({ suburb: option.s, postcode: option.p, state: option.t, lat: option.lat, lng: option.lng });
    setOpen(false);
  };

  return (
    <div className="relative">
      <MapPin className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        id={id}
        required={required}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          ensureLoaded();
        }}
        onFocus={() => {
          setOpen(true);
          ensureLoaded();
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
        autoComplete="off"
        className="pl-9"
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg"
          role="listbox"
          aria-label="Australian suburbs"
          data-testid={testId + "-options"}
        >
          {suggestions.map((option) => (
            <button
              key={option.s + "-" + option.p + "-" + option.t}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
              className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-sky-50"
              data-testid={testId + "-option-" + option.p + "-" + option.s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
            >
              <span className="font-medium text-slate-700">{option.s}</span>
              <span className="ml-3 text-xs text-slate-400">{option.t} {option.p}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
